import { apiPost, apiPostV2 } from "~/shared/api/client";

import type { LegacyGenerationJob, LegacyJobsByMessagesResponse } from "./types";

// Метаданные референса живого потока. sha256 считается на
// фронте при загрузке attachment — бэк наполняет реестр `references` (дедуп по
// хэшу) и провенанс-связь, не скачивая файл повторно. Поля прокидываются рядом
// с inputImageUrls, а не вместо них — payload провайдера не меняется.
export interface ReferenceMetadata {
    sha256: string;
    kind: string;
    bytes: number;
    url: string;
    originalName?: string | null;
}

export interface StartGenerationPayload {
    chatUuid: string;
    messageId: string;
    modelName: string;
    generationMode: string;
    settings: {
        // Опционально: режимы без соотношения сторон (omni / motion control —
        // ориентация по референсам) его не шлют, иначе бэк не найдёт провайдера.
        aspectRatio?: string;
        // Опционально по той же причине: у модели без оси разрешения (Nano Banana
        // первой версии) размер задаёт соотношение сторон, конфиги хранят NULL.
        resolution?: number;
        durationSeconds?: number;
        withSound?: boolean;
        seed?: number;
        // Kling Motion Control: "video" | "image"; camel→snake даст
        // character_orientation в GenerationSettings бэка.
        characterOrientation?: string;
        // Topaz Upscale: во сколько раз увеличиваем; camel→snake даст
        // upscale_factor в GenerationSettings бэка.
        upscaleFactor?: number;
        // Сколько результатов вернуть за одну генерацию. Бэк маршрутизирует
        // только на провайдеров, которые столько отдадут (нативно или
        // эмуляцией), и списывает за доставленное; потолок режима приходит в
        // настройках (`modes[].maxBatchSize`), плитки сверх него бар блокирует.
        batchSize?: number;
    };
    // Для режимов с роль-слотами порядок значим: `inputImageUrls[i]`
    // — картинка роли `mode.imageRoles[i]`, бэк раскладывает их по каналам
    // провайдера, а не угадывает первый/последний кадр по порядку загрузки.
    inputImageUrls?: string[];
    inputVideoUrls?: string[];
    inputAudioUrls?: string[];
    // Референсные картинки — свой канал, не кадры: кадр задаёт начало/конец
    // видео, референс описывает, что должно быть в кадре, и упоминается в
    // промпте. Бэк кладёт их отдельным полем `GenerationInput`, а адаптер
    // раскрывает в нативную роль провайдера (`refer_image` у Kling Omni).
    referenceImageUrls?: string[];
    references?: ReferenceMetadata[];
    // Элементы библиотеки — отдельный канал: бэк разворачивает id в
    // snapshot (имя/описание/картинки) и отдаёт провайдеру как элементы, не как
    // обычные референс-картинки.
    referenceElementIds?: string[];
}

export interface StartGenerationResponse {
    generationJobId: string;
}

// POST /v2/artifacts. Backend ChatClient resolves message_id either from
// the new `chat_messages` table (v2) or the legacy `messages` table
// (fallback). RequestIdentityHeaders still wants workspace/project/scene,
// so we drop down to the raw apiPost helper to inject context — apiPostV2
// doesn't take a context arg.
export async function startGeneration(
    payload: StartGenerationPayload,
    context: { workspaceId: string; projectId?: string; sceneId?: string },
): Promise<StartGenerationResponse> {
    return apiPost<StartGenerationResponse>(
        "/v2/artifacts",
        {
            chatUuid: payload.chatUuid,
            messageId: payload.messageId,
            modelName: payload.modelName,
            imageMode: payload.generationMode,
            settings: payload.settings,
            inputImageUrls: payload.inputImageUrls ?? [],
            inputVideoUrls: payload.inputVideoUrls ?? [],
            inputAudioUrls: payload.inputAudioUrls ?? [],
            referenceImageUrls: payload.referenceImageUrls ?? [],
            references: payload.references ?? [],
            referenceElementIds: payload.referenceElementIds ?? [],
        },
        context,
    );
}

export async function fetchJobsByMessages(messageIds: string[]): Promise<LegacyJobsByMessagesResponse> {
    return apiPostV2<LegacyJobsByMessagesResponse>("/artifacts/generation-jobs/by-messages", {
        messageIds,
    });
}

export type JobsByMessageMap = Record<string, LegacyGenerationJob>;
